import { takeLatest, put, call } from 'redux-saga/effects';
import i18next from 'i18next';
import { resolveError } from 'utils/resolveError';
import api, { downloadFile, newRequestId } from 'api/client';
import * as license from 'reducer/rLicense';
import * as message from 'reducer/rMessageBox';

/**
 * 라이선스 조회·발급·변경.
 *
 * <p>조회는 포탈이 원장을 join 해서 읽고, 변경은 포탈이 발급 서버 API 를 거친다. 화면은 그
 * 차이를 몰라도 된다.</p>
 *
 * <p>TFL 화면에서 이관하며 바뀐 것 셋 —
 * (1) 목록 응답이 `{items, total}` 로 감싸져 온다,
 * (2) 페이지가 limit/offset 이 아니라 page/size 다,
 * (3) 수정이 한 엔드포인트가 아니라 <b>인상·연장·무효화 세 개</b>다. 정체성 값(제품·고객사·
 * 타입)은 서명된 파일에 박혀 있어 수정 엔드포인트가 없다.</p>
 */
export function* sLicense() {
    yield takeLatest(license.fetchLicenseRequest, fetchLicense);
    yield takeLatest(license.fetchAddLicenseRequest, fetchAddLicense);
    yield takeLatest(license.fetchAddBundleLicenseRequest, fetchAddBundleLicense);
    yield takeLatest(license.fetchLicenseDetailRequest, fetchLicenseDetail);
    yield takeLatest(license.fetchUpdateLicenseRequest, fetchUpdateLicense);
    yield takeLatest(license.fetchSearchLicenseRequest, fetchSearchLicense);
    yield takeLatest(license.fetchDownloadLicenseRequest, fetchDownloadLicense);
}

/** limit/offset 을 쓰던 화면을 그대로 두고 여기서 page/size 로 바꾼다. */
function toPageParams({ limit, offset, keyword, sortBy, sortOrder }) {
    const size = limit && limit > 0 ? limit : 20;
    const params = new URLSearchParams({
        page: String(Math.floor((offset || 0) / size)),
        size: String(size),
    });
    if (keyword) {
        params.set('keyword', keyword);
    }
    if (sortBy) {
        params.set('sortBy', sortBy);
    }
    if (sortOrder) {
        params.set('sortOrder', sortOrder);
    }
    return params;
}

function* loadList(action, onSuccess, onFailure) {
    try {
        const params = toPageParams(action.payload ?? {});
        const response = yield call(() => api.get(`/licenses?${params.toString()}`));

        if (response.data.resultCode === '0000') {
            const page = response.data.data ?? {};
            // 리듀서는 예전 모양({data, totalCount})을 기대한다. 화면을 건드리지 않으려고
            // 여기서 맞춰 준다.
            yield put(onSuccess({ data: page.items ?? [], totalCount: page.total ?? 0 }));
        } else {
            yield put(onFailure(resolveError(response.data)));
        }
    } catch (error) {
        yield put(onFailure(resolveError(error.response?.data)
            || i18next.t('common.unexpectedError')));
    } finally {
        yield put(license.setLoading(false));
    }
}

function* fetchLicense(action) {
    yield* loadList(action, license.fetchLicenseSuccess, license.fetchLicenseFailure);
}

function* fetchSearchLicense(action) {
    yield* loadList(action, license.fetchSearchLicenseSuccess, license.fetchSearchLiceneseFailure);
}

/**
 * 발급.
 *
 * <p><b>요청 식별자를 만들어 보낸다.</b> 응답이 늦어 사용자가 다시 누르거나 재시도할 때 같은
 * 값이면 발급 서버가 이중 발급을 막는다. 그래서 화면이 만들어야 하고, 서버가 만들면 재시도가
 * 항상 새 값이 되어 보호가 사라진다.</p>
 */
function* fetchAddLicense(action) {
    try {
        const requestId = action.payload?.requestId || newRequestId();
        const response = yield call(() =>
            api.post('/licenses', { ...action.payload, requestId })
        );

        if (response.data.resultCode !== '0000') {
            yield put(license.fetchAddLicenseFailure(response.data.resultMessage));
            yield put(message.fetchMessageBoxRequest({
                isError: true, isSuccess: false,
                resultCode: response.data.resultCode,
                message: resolveError(response.data)
                    || i18next.t('license_msg.issueFailed'),
            }));
            return;
        }

        const issued = response.data.data;
        yield put(license.fetchAddLicenseSuccess(response.data));
        yield put(license.fetchDownloadLicenseRequest({ licenseId: issued.licenseId }));
    } catch (error) {
        const msg = error.response?.status === 401
            ? i18next.t('common.sessionExpired')
            : resolveError(error.response?.data)
                || i18next.t('common.unexpectedError');
        yield put(license.fetchAddLicenseFailure(msg));
        yield put(message.fetchMessageBoxRequest({
            isError: true, isSuccess: false, resultCode: '9999', message: msg,
        }));
    } finally {
        yield put(license.setLoading(false));
    }
}

/**
 * 여러 종류 발급.
 *
 * <p><b>포탈이 발급을 여러 번 호출한다.</b> 발급 서버에 묶음 엔드포인트를 두지 않았다 —
 * 번들은 포탈 개념이고, 원자적으로 처리하려면 발급 서버가 그 개념을 알아야 한다. tDrive 묶음은
 * 포탈 서버의 묶음 엔드포인트가 그 반복을 맡는다({@link issueDriveBundle}).</p>
 *
 * <p>ZIP 으로 묶어 내려주던 동작은 없앴다. 각 발급이 별 요청이라 한 번에 묶을 지점이 없고,
 * 브라우저가 연속 다운로드를 막는다. <b>발급만 하고 파일은 목록에서 내려받게 한다</b> —
 * 부분 실패 시 어디까지 됐는지도 목록에서 보인다.</p>
 */
function* fetchAddBundleLicense(action) {
    const { licenses } = action.payload;
    // tDrive 묶음은 서버가 오피스 몫까지 한 번에 낸다. 같은 묶음 값을 요청 번호로 쓰므로
    // 다시 누르면 서버가 빠진 건만 마저 낸다.
    if (licenses[0]?.bundleGroupId) {
        yield* issueDriveBundle(licenses);
        return;
    }
    const issued = [];
    const failed = [];
    let unknown = false;

    try {
        // 발급을 시작하기 전에 한도를 한꺼번에 묻는다.
        //
        // 발급은 한 건씩 나간다 - 발급 서버에 여러 종류라는 개념이 없어서다. 그래서 그때그때
        // 검사하면 **앞의 것은 발급되고 뒤의 것만 거부된다.** 사용자는 한 번 눌렀는데 절반만
        // 나간 상태가 남는다. 하나라도 모자라면 여기서 멈춘다.
        //
        // 화면이 가진 잔여값으로 판단하지 않는다. 그 값은 모달을 열 때 받은 것이라, 같은
        // 계약을 쓰는 다른 사용자가 그 사이 발급했으면 낡았다. 서버가 그 자리에서 센다.
        //
        // 요청 번호도 함께 보낸다. 다시 보내는 건은 이미 나갔거나 수량을 잡아 둔 것이라 서버가
        // 새로 세지 않는다 - 세면 한도를 끝까지 쓴 계약에서 다시 보내기가 막힌다.
        const check = yield call(() =>
            api.post('/licenses/quota-check', {
                items: licenses.map(({ contractId, productId, licenseType, limit, requestId }) =>
                    ({ contractId, productId, licenseType, limit, requestId })),
            })
        );
        if (check.data?.resultCode !== '0000') {
            const reason = resolveError(check.data)
                || i18next.t('license_msg.bundleFailed');
            yield put(license.fetchAddBundleLicenseFailure(reason));
            yield put(message.fetchMessageBoxRequest({
                isError: true, isSuccess: false, resultCode: '9999', message: reason,
            }));
            return;
        }

        for (const item of licenses) {
            const requestId = item.requestId || newRequestId();
            try {
                const response = yield call(() =>
                    api.post('/licenses', { ...item, requestId })
                );
                if (response.data.resultCode === '0000') {
                    issued.push(response.data.data.licenseId);
                } else {
                    failed.push(resolveError(response.data));
                }
            } catch (error) {
                unknown = unknown || resultUnknown(error);
                failed.push(resolveError(error.response?.data)
                    || i18next.t('license_msg.issueFailed'));
            }
        }

        if (failed.length === 0) {
            // 곧바로 내려받지 않는다. 브라우저가 막으면 아무 일도 없던 것처럼 보이고
            // 받을 방법도 사라진다. 확인 창을 띄워 사용자가 누른 것으로 만든다.
            yield put(license.fetchAddBundleLicenseSuccess(issued));
            yield put(message.fetchMessageBoxRequest({
                isError: false, isSuccess: true, resultCode: '0000',
                message: issued.length === 1
                    ? i18next.t('license_msg.issueSuccess')
                    : i18next.t('license_msg.multiSuccess', { count: issued.length }),
            }));
            return;
        }

        // 일부만 성공한 상태를 감추지 않는다. 발급된 것은 유지되고 목록에 보인다.
        //
        // 실패 사유는 서버가 준 문장이다. 어느 라이선스 타입이 막혔는지가 그 안에 있다 -
        // 묶음은 타입이 여럿이라 "한도를 초과합니다" 만으로는 어느 쪽인지 알 수 없었다.
        const summary = i18next.t('license_msg.partialResult', {
            issued: issued.length,
            total: licenses.length,
            reasons: failed.join(', '),
        });
        if (issued.length > 0 || unknown) {
            yield put(license.notePartiallyIssued());
        }
        yield put(license.fetchAddBundleLicenseFailure(summary));
        yield put(message.fetchMessageBoxRequest({
            isError: true, isSuccess: false, resultCode: '9999', message: summary,
        }));
    } catch (error) {
        // 한도 확인이 응답 없이 끊긴 경우다(발급은 건마다 따로 받아 둔다). 실패로 끝내지 않으면
        // 발급 중 상태가 남아 버튼이 풀리지 않는다.
        const reason = error.response?.status === 401
            ? i18next.t('common.sessionExpired')
            : resolveError(error.response?.data) || i18next.t('license_msg.bundleFailed');
        yield put(license.fetchAddBundleLicenseFailure(reason));
        yield put(message.fetchMessageBoxRequest({
            isError: true, isSuccess: false, resultCode: '9999', message: reason,
        }));
    } finally {
        yield put(license.setLoading(false));
    }
}

const PRODUCT_ID_TFD = 2;

/**
 * 응답을 받지 못했거나 서버 쪽 오류(5xx, 프록시 오류)라서 발급됐는지 알 수 없는 실패.
 *
 * <p>4xx 는 서버가 거절한 것이라 아무것도 나가지 않았다. 그 밖에는 나갔을 수 있으므로
 * 나간 것과 같이 다룬다 - 폼이 같은 번호, 같은 내용으로만 다시 보내게 된다.</p>
 */
const resultUnknown = (error) => !error.response || error.response.status >= 500;

/**
 * 화면이 만든 묶음 목록을 서버의 묶음 요청으로 바꾼다.
 *
 * <p>공통 값은 tDrive 줄에서 가져온다. 오피스 줄은 서버가 다시 만든다 - PWL 을 빼는 것까지
 * 서버 규칙이라 여기서 보낸 값을 쓰지 않는다.</p>
 */
const toBundleRequest = (licenses) => {
    const drive = licenses.filter(l => l.productId === PRODUCT_ID_TFD);
    const base = drive[0];
    return {
        requestId: base.bundleGroupId,
        contractId: base.contractId,
        clientId: base.clientId,
        category: base.category,
        subCategory: base.subCategory,
        drive: drive.map(({ licenseType, limit }) => ({ licenseType, limit })),
        options: base.options,
        onlineAuth: base.onlineAuth,
        startDate: base.startDate,
        endDate: base.endDate,
        description: base.description,
    };
};


/**
 * tDrive 묶음 발급. 한도 확인과 건별 발급을 서버가 한 요청 안에서 한다.
 *
 * <p>일부만 나가면 서버가 실패 코드와 함께 건별 결과를 준다. 나간 것이 있으면 세대를 올려
 * 목록이 다시 읽히게 하고, 폼은 같은 내용으로만 다시 보내게 막는다 - 그래야 같은 번호로 간다.</p>
 */
function* issueDriveBundle(licenses) {
    try {
        const response = yield call(() => api.post('/licenses/bundle', toBundleRequest(licenses)));
        const items = response.data?.data?.licenses ?? [];
        const issued = items.filter(item => item.status === 'ISSUED').map(item => item.license.licenseId);

        if (response.data?.resultCode === '0000') {
            yield put(license.fetchAddBundleLicenseSuccess(issued));
            yield put(message.fetchMessageBoxRequest({
                isError: false, isSuccess: true, resultCode: '0000',
                message: i18next.t('license_msg.multiSuccess', { count: issued.length }),
            }));
            return;
        }

        let reason;
        if (items.length > 0) {
            reason = i18next.t('license_msg.partialResult', {
                issued: issued.length,
                total: items.length,
                reasons: items.filter(item => item.status === 'FAILED')
                    .map(item => resolveError(item.reason)).join(', '),
            });
            if (issued.length > 0) {
                yield put(license.notePartiallyIssued());
            }
        } else {
            reason = resolveError(response.data) || i18next.t('license_msg.bundleFailed');
        }
        yield put(license.fetchAddBundleLicenseFailure(reason));
        yield put(message.fetchMessageBoxRequest({
            isError: true, isSuccess: false, resultCode: '9999', message: reason,
        }));
    } catch (error) {
        // 응답이 끊겼으면 서버에서는 묶음이 나갔을 수 있다. 내용을 바꿔 새 번호로 보내면
        // 이미 나간 tDrive 까지 다시 나가므로 같은 내용으로만 다시 보내게 한다.
        if (resultUnknown(error)) {
            yield put(license.notePartiallyIssued());
        }
        const reason = error.response?.status === 401
            ? i18next.t('common.sessionExpired')
            : resolveError(error.response?.data) || i18next.t('license_msg.bundleFailed');
        yield put(license.fetchAddBundleLicenseFailure(reason));
        yield put(message.fetchMessageBoxRequest({
            isError: true, isSuccess: false, resultCode: '9999', message: reason,
        }));
    } finally {
        yield put(license.setLoading(false));
    }
}

function* fetchLicenseDetail(action) {
    try {
        const { licenseId } = action.payload;
        if (!licenseId || licenseId <= 0) {
            yield put(license.fetchLicenseDetailFailure(i18next.t('license_msg.invalidId')));
            return;
        }

        const response = yield call(() => api.get(`/licenses/${licenseId}`));

        if (response.data.resultCode === '0000' && response.data.data) {
            yield put(license.fetchLicenseDetailSuccess(response.data.data));
        } else {
            yield put(license.fetchLicenseDetailFailure(
                resolveError(response.data) || i18next.t('license_msg.loadFailed')));
        }
    } catch (error) {
        yield put(license.fetchLicenseDetailFailure(
            resolveError(error.response?.data)
            || i18next.t('license_msg.loadFailed')));
    } finally {
        yield put(license.setLoading(false));
    }
}

/**
 * 수정. 방향이 제약된다.
 *
 * <p>엔드포인트가 셋으로 갈려 있다 — <b>제한 수치는 올리기만, 만료일은 늘리기만, 상태는
 * 무효화만</b>. 발급 서버가 반대 방향을 거부한다. 내리거나 앞당기면 이미 배포된 파일과
 * 원장이 어긋나고, 제품은 파일을 믿기 때문에 조용히 불일치가 생긴다.</p>
 */
function* fetchUpdateLicense(action) {
    const { licenseId, data } = action.payload;
    try {
        const calls = [];
        if (data.limitValue !== undefined && data.limitValue !== null) {
            calls.push(['limit', { value: data.limitValue }]);
        }
        if (data.endDate) {
            calls.push(['end-date', { endDate: data.endDate }]);
        }
        if (data.status === 'INACTIVE') {
            calls.push(['deactivate', {}]);
        }
        if (calls.length === 0) {
            yield put(license.fetchUpdateLicenseFailure(i18next.t('license_msg.updateFailed')));
            return;
        }

        for (const [path, body] of calls) {
            const url = `/licenses/${licenseId}/${path}`;
            // 무효화는 본문이 없다. PATCH 로 통일해 화면이 경로만 알면 되게 한다.
            const response = yield call(() => api.patch(url, body));
            if (response.data.resultCode !== '0000') {
                yield put(license.fetchUpdateLicenseFailure(response.data.resultMessage));
                yield put(message.fetchMessageBoxRequest({
                    isError: true, isSuccess: false,
                    resultCode: response.data.resultCode,
                    message: resolveError(response.data)
                        || i18next.t('license_msg.updateFailed'),
                }));
                return;
            }
        }

        yield put(license.fetchUpdateLicenseSuccess(data));
        yield put(message.fetchMessageBoxRequest({
            isError: false, isSuccess: true, resultCode: '0000',
            message: i18next.t('license_msg.updateSuccess'),
        }));
    } catch (error) {
        const msg = error.response?.status === 401
            ? i18next.t('common.sessionExpired')
            : resolveError(error.response?.data)
                || i18next.t('common.unexpectedError');
        yield put(license.fetchUpdateLicenseFailure(msg));
        yield put(message.fetchMessageBoxRequest({
            isError: true, isSuccess: false, resultCode: '9999', message: msg,
        }));
    } finally {
        yield put(license.setLoading(false));
    }
}

/**
 * 파일 다운로드.
 *
 * <p>포탈이 보관한 파일을 그대로 받는다. <b>다운로드가 재발급이 아니다</b> — 다시 발급하면
 * 원장에 발급 기록이 또 쌓이고 사용자는 같은 라이선스에 파일이 여러 개인 상태를 본다.</p>
 */
function* fetchDownloadLicense(action) {
    try {
        const { licenseId } = action.payload;
        yield call(() => downloadFile(`/licenses/${licenseId}/file`, `license-${licenseId}.tfl`));
    } catch (error) {
        yield put(message.fetchMessageBoxRequest({
            isError: true, isSuccess: false, resultCode: '9999',
            message: i18next.t('license_msg.downloadFailed'),
        }));
    } finally {
        yield put(license.setLoading(false));
    }
}
